<template>
  <svg
    id="signer-document"
    :height="contentMainHeight"
    :viewbox="`0 0 ${contentMainWidth} ${contentMainHeight}`"
    :width="contentMainWidth"
    focusable="false"
    @click="emit('svg-clicked', $event)"
  >
    <g data-view-id="doc-image-container" data-view-name="DocumentsView" transform="matrix(1,0,0,1, 0, 0)">
      <g
        id="DocumentView"
        :transform="`matrix(1,0,0,1, ${leftAndRightDocumentMargin}, ${leftAndRightDocumentMargin})`"
        data-view-id="c241"
        data-view-name="DocumentView"
        @load="emit('doc-view-load', $event)"
      >
        <!-- Page Views -->
        <g
          v-for="(pdf, key) in localDocumentImages"
          :id="`pdf-${key}`"
          :key="`pdf-${key}`"
          :transform="`matrix(1,0,0,1, 0, ${pdf.position})`"
          data-view-name="PageView"
        >
          <rect :height="docImage.height" :width="pageViewRectangle" fill="transparent" stroke="0" stroke-width="1.4" x="-1" y="-1"/>
          <image :id="`document-image-${key}`" :height="docImage.height" :width="docImage.width" :xlink:href="stagePageImage(pdf.path)" @load="emit('img-load', $event)"/>
          <text :y="docImage.height + 10" class="font-sans" fill="#666666" font-size="8">
            {{ documentFileName }}
          </text>
          <text :x="docImage.width - 20" :y="docImage.height + 10" class="font-sans" fill="#666666" font-size="8">
            {{ key + 1 }} of {{ localDocumentImages.length }}
          </text>
        </g>

        <!-- Data Fields -->
        <DocumentFieldLayer
          :determine-display-font-size="determineDisplayFontSize"
          :determine-text-lines="determineTextLines"
          :determine-y-text-offset="determineYTextOffset"
          :element-class-object="elementClassObject"
          :element-fill-color="elementFillColor"
          :fields="fields"
          :tippy-content="tippyContent"
          @check-coords="emit('check-coords', $event)"
          @select-element="(event, field) => emit('select-element', event, field)"
          @signer-clicked="emit('signer-clicked', $event)"
        />

        <!-- Snap alignment guides (same coordinate space as the fields) -->
        <line
          v-for="(guide, guideIndex) in snapGuides"
          :key="`snap-guide-${guideIndex}`"
          :x1="guide.orientation === 'vertical' ? guide.position : guide.start"
          :x2="guide.orientation === 'vertical' ? guide.position : guide.end"
          :y1="guide.orientation === 'vertical' ? guide.start : guide.position"
          :y2="guide.orientation === 'vertical' ? guide.end : guide.position"
          class="snap-guide-line"
          pointer-events="none"
        />
      </g>

      <!-- Drag handles -->
      <slot name="drag-handles"/>
    </g>
  </svg>
</template>

<script lang="ts" setup>
import type {ESignerFieldData} from '@/types/esign';
import type {SnapGuide} from '@/utils/esignSnapMath';

import DocumentFieldLayer from '@/components/documentsV2/DocumentFieldLayer.vue';
import {toTwicPicsUrl} from '@/utils/twicpics';

/**
 * SVG images take no srcset, so the editor gets the page at its stored size
 * (never upscaled) re-encoded by TwicPics: full fidelity for placing fields,
 * a fraction of the PNG bytes.
 */
const stagePageImage = (path: string): string => toTwicPicsUrl(path, {max: [1632, 1632]}) ?? path;

type DocumentImage = {
  position: number;
  path: string;
};

type DocImageDims = {
  height: number;
  width: number;
};

/* eslint-disable no-unused-vars */
withDefaults(defineProps<{
  contentMainHeight: number;
  contentMainWidth: number;
  docImage: DocImageDims;
  documentFileName: string;
  fields: ESignerFieldData[];
  leftAndRightDocumentMargin: number;
  localDocumentImages: DocumentImage[];
  pageViewRectangle: number;
  snapGuides?: SnapGuide[];
  tippyContent: (data: ESignerFieldData) => string;
  elementClassObject: (data: ESignerFieldData) => unknown;
  elementFillColor: (data: ESignerFieldData) => string;
  determineDisplayFontSize: (data: ESignerFieldData) => number;
  determineYTextOffset: (data: ESignerFieldData) => number;
  determineTextLines: (data: ESignerFieldData) => string[];
}>(), {
  snapGuides: () => [],
});
/* eslint-enable no-unused-vars */

const emit = defineEmits<{
  'svg-clicked': [event: MouseEvent];
  'doc-view-load': [event: Event];
  'img-load': [event: Event];
  'check-coords': [event: MouseEvent];
  'select-element': [event: MouseEvent, field: ESignerFieldData];
  'signer-clicked': [field: ESignerFieldData];
}>();
</script>

<style scoped>
.snap-guide-line {
  stroke: #e91e63;
  stroke-dasharray: 5 3;
  stroke-width: 1;
}
</style>

